import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SaveIcon, PlusIcon, TrashIcon, UserIcon, PhoneIcon, HomeIcon, MapPinIcon, CalendarIcon, DollarSignIcon, FileTextIcon, HashIcon, CheckCircleIcon } from 'lucide-react';
import { BuyerLead } from '../../pages/rental/RentalLeadsKanban';
interface NewLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (lead: BuyerLead) => void;
}
const ROOM_OPTIONS = [1, 2, 3, 4, '4+'];
const BATHROOM_OPTIONS = [1, 2, 3, 4, '4+'];
const SOURCE_OPTIONS = ['ID proprietate', 'networking', 'familie', 'coleg', 'baner', 'promovare individuala'];
const PROPERTY_TYPES = ['Apartament', 'Casă', 'Vilă', 'Penthouse', 'Studio'];
export function NewLeadModal({
  isOpen,
  onClose,
  onSubmit
}: NewLeadModalProps) {
  // Form State
  const [leadId, setLeadId] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [source, setSource] = useState(SOURCE_OPTIONS[0]);
  const [propertyId, setPropertyId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [propertyType, setPropertyType] = useState(PROPERTY_TYPES[0]);
  const [selectedRooms, setSelectedRooms] = useState<(number | string)[]>([]);
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [surfaceMin, setSurfaceMin] = useState('');
  const [surfaceMax, setSurfaceMax] = useState('');
  const [zones, setZones] = useState<string[]>(['', '', '']);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [memo, setMemo] = useState('');
  const [whoWillMove, setWhoWillMove] = useState('');
  const [acquisitionReason, setAcquisitionReason] = useState('');
  // Optional fields state
  const [excludedFloors, setExcludedFloors] = useState('');
  const [maxFloors, setMaxFloors] = useState('');
  const [selectedBathrooms, setSelectedBathrooms] = useState<(number | string)[]>([]);
  const [constructionYear, setConstructionYear] = useState('');
  // Validation State
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Initialize ID and Date on open
  useEffect(() => {
    if (isOpen) {
      const randomId = Math.floor(Math.random() * 90000) + 10000;
      setLeadId(`LI${randomId}`);
      const now = new Date();
      setDateTime(now.toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }));
      // Reset form
      setSource(SOURCE_OPTIONS[0]);
      setPropertyId('');
      setName('');
      setPhone('');
      setPropertyType(PROPERTY_TYPES[0]);
      setSelectedRooms([]);
      setBudgetMin('');
      setBudgetMax('');
      setSurfaceMin('');
      setSurfaceMax('');
      setZones(['', '', '']);
      setPaymentMethods([]);
      setMemo('');
      setWhoWillMove('');
      setAcquisitionReason('');
      setExcludedFloors('');
      setMaxFloors('');
      setSelectedBathrooms([]);
      setConstructionYear('');
      setErrors({});
    }
  }, [isOpen]);
  const handleRoomToggle = (room: number | string) => {
    if (selectedRooms.includes(room)) {
      setSelectedRooms(selectedRooms.filter(r => r !== room));
    } else {
      setSelectedRooms([...selectedRooms, room]);
    }
  };
  const handleBathroomToggle = (bathroom: number | string) => {
    if (selectedBathrooms.includes(bathroom)) {
      setSelectedBathrooms(selectedBathrooms.filter(b => b !== bathroom));
    } else {
      setSelectedBathrooms([...selectedBathrooms, bathroom]);
    }
  };
  const handlePaymentMethodToggle = (method: string) => {
    if (paymentMethods.includes(method)) {
      setPaymentMethods(paymentMethods.filter(m => m !== method));
    } else {
      setPaymentMethods([...paymentMethods, method]);
    }
  };
  const handleZoneChange = (index: number, value: string) => {
    const newZones = [...zones];
    newZones[index] = value;
    setZones(newZones);
  };
  const addZone = () => {
    if (zones.length < 5) {
      setZones([...zones, '']);
    }
  };
  const removeZone = (index: number) => {
    if (zones.length > 3) {
      const newZones = zones.filter((_, i) => i !== index);
      setZones(newZones);
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Numele este obligatoriu';
    if (!phone.trim()) {
      newErrors.phone = 'Telefonul este obligatoriu';
    } else if (!/^07\d{8}$/.test(phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Format invalid (ex: 0712345678)';
    }
    if (!budgetMin) {
      newErrors.budgetMin = 'Bugetul minim este obligatoriu';
    }
    if (!budgetMax) {
      newErrors.budgetMax = 'Bugetul maxim este obligatoriu';
    }
    if (budgetMin && budgetMax && parseFloat(budgetMax) < parseFloat(budgetMin)) {
      newErrors.budgetMax = 'Bugetul maxim trebuie să fie mai mare sau egal cu minimul';
    }
    if (!surfaceMin) {
      newErrors.surfaceMin = 'Suprafața minimă este obligatorie';
    }
    if (!surfaceMax) {
      newErrors.surfaceMax = 'Suprafața maximă este obligatorie';
    }
    if (surfaceMin && surfaceMax && parseFloat(surfaceMax) < parseFloat(surfaceMin)) {
      newErrors.surfaceMax = 'Suprafața maximă trebuie să fie mai mare sau egală cu minima';
    }
    if (selectedRooms.length === 0) newErrors.rooms = 'Selectează cel puțin o opțiune';
    const filledZones = zones.filter(z => z.trim().length > 0);
    if (filledZones.length < 3) {
      newErrors.zones = 'Minim 3 zone sunt obligatorii';
    }
    if (source === 'ID proprietate' && !propertyId.trim()) {
      newErrors.propertyId = 'ID-ul proprietății este obligatoriu';
    }
    if (!whoWillMove.trim()) {
      newErrors.whoWillMove = 'Acest câmp este obligatoriu';
    }
    if (!acquisitionReason.trim()) {
      newErrors.acquisitionReason = 'Acest câmp este obligatoriu';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) return;
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const time = now.toTimeString().slice(0, 5);
    const briefMemo = memo.trim() ? memo : `Lead ${source === 'ID proprietate' ? `pentru proprietatea ${propertyId}` : `din sursa: ${source}`}. Caută ${propertyType}, ${selectedRooms.join('/')} camere, buget ${budgetMin}-${budgetMax} EUR în zonele: ${zones.filter(z => z.trim()).join(', ')}.`;
    const newLead: BuyerLead = {
      leadId,
      createdAt: now.toISOString(),
      entryDate: today,
      entryTime: time,
      name,
      phone,
      propertyId: source === 'ID proprietate' ? propertyId : undefined,
      leadSource: source !== 'ID proprietate' ? source : undefined,
      callCenterMemo: briefMemo,
      callCompleted: false,
      callStatus: 'nou',
      fullName: name,
      propertyType,
      rooms: typeof selectedRooms[0] === 'number' ? selectedRooms[0] : parseInt(selectedRooms[0] as string) || 1,
      budgetMin: parseInt(budgetMin),
      budgetMax: parseInt(budgetMax),
      surfaceMin: parseInt(surfaceMin),
      surfaceMax: parseInt(surfaceMax),
      zones: zones.filter(z => z.trim()),
      paymentMethods: paymentMethods.length > 0 ? paymentMethods : undefined,
      whoWillMove: whoWillMove.trim() || undefined,
      acquisitionReason: acquisitionReason.trim() || undefined,
      excludedFloors: excludedFloors.trim() || undefined,
      maxFloors: maxFloors ? parseInt(maxFloors) : undefined,
      bathrooms: selectedBathrooms.length > 0 ? typeof selectedBathrooms[0] === 'number' ? selectedBathrooms[0] : parseInt(selectedBathrooms[0] as string) || undefined : undefined,
      constructionYear: constructionYear ? parseInt(constructionYear) : undefined,
      qualified: false,
      actionHistory: [{
        action: 'Lead introdus în sistem',
        timestamp: `${today} ${time}`,
        agent: 'Agent Curent'
      }]
    };
    onSubmit(newLead);
    onClose();
  };
  return <AnimatePresence data-id="element-4388">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100" data-id="element-4389" />

          {/* Sidebar Panel - matching LeadDetailsPanel style */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-110 overflow-y-auto" data-id="element-4390">
            {/* Header with gradient - matching LeadDetailsPanel */}
            <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg" data-id="element-4391">
              <div className="flex items-center justify-between" data-id="element-4392">
                <div data-id="element-4393">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-4394">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-4395">
                      <UserIcon className="w-5 h-5 text-white" data-id="element-4396" />
                    </div>
                    <div data-id="element-4397">
                      <h3 className="text-xl font-bold" data-id="element-4398">Lead Nou</h3>
                      <p className="text-sm text-blue-200" data-id="element-4399">
                        Completează detaliile noului lead
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-4400">
                  <XIcon className="w-6 h-6 text-white" data-id="element-4401" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-6" data-id="element-4402">
              {/* System Info Card - matching LeadDetailsPanel style */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4403">
                <div className="flex items-center space-x-3 mb-4" data-id="element-4404">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center" data-id="element-4405">
                    <HashIcon className="w-6 h-6 text-white" data-id="element-4406" />
                  </div>
                  <div data-id="element-4407">
                    <div className="flex items-center space-x-2 mb-1" data-id="element-4408">
                      <span className="text-lg font-bold text-[#132C53]" data-id="element-4409">
                        {leadId}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold" data-id="element-4410">
                        Nou
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-600" data-id="element-4411">
                      <CalendarIcon className="w-4 h-4 text-purple-500" data-id="element-4412" />
                      <span data-id="element-4413">{dateTime}</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Info Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4414">
                <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-4415">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-4416">
                    <UserIcon className="w-5 h-5 text-blue-600" data-id="element-4417" />
                  </div>
                  <span data-id="element-4418">Date Contact</span>
                </h5>

                <div className="space-y-4" data-id="element-4419">
                  <div data-id="element-4420">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4421">
                      Nume Complet <span className="text-red-500" data-id="element-4422">*</span>
                    </label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Popescu Ion" data-id="element-4423" />
                    {errors.name && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-4424">
                        <span data-id="element-4425">⚠️</span>
                        <span data-id="element-4426">{errors.name}</span>
                      </p>}
                  </div>

                  <div data-id="element-4427">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4428">
                      Telefon <span className="text-red-500" data-id="element-4429">*</span>
                    </label>
                    <div className="relative" data-id="element-4430">
                      <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4431" />
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="07XXXXXXXX" data-id="element-4432" />
                    </div>
                    {errors.phone && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-4433">
                        <span data-id="element-4434">⚠️</span>
                        <span data-id="element-4435">{errors.phone}</span>
                      </p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4436">
                    <div data-id="element-4437">
                      <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4438">
                        Sursa Lead
                      </label>
                      <select value={source} onChange={e => setSource(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white" data-id="element-4439">
                        {SOURCE_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-4440">
                            {opt}
                          </option>)}
                      </select>
                    </div>

                    {source === 'ID proprietate' && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-4441">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4442">
                          ID Proprietate <span className="text-red-500" data-id="element-4443">*</span>
                        </label>
                        <input type="text" value={propertyId} onChange={e => setPropertyId(e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none ${errors.propertyId ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: P12345" data-id="element-4444" />
                        {errors.propertyId && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-4445">
                            <span data-id="element-4446">⚠️</span>
                            <span data-id="element-4447">{errors.propertyId}</span>
                          </p>}
                      </motion.div>}
                  </div>
                </div>
              </motion.div>

              {/* Requirements Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4448">
                <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-4449">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-4450">
                    <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-4451" />
                  </div>
                  <span data-id="element-4452">Cerințe Imobiliare</span>
                </h5>

                <div className="space-y-4" data-id="element-4453">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4454">
                    <div data-id="element-4455">
                      <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4456">
                        Tip Imobil
                      </label>
                      <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none bg-white" data-id="element-4457">
                        {PROPERTY_TYPES.map(type => <option key={type} value={type} data-id="element-4458">
                            {type}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-4459">
                      <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4460">
                        Nr. Camere <span className="text-red-500" data-id="element-4461">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-4462">
                        {ROOM_OPTIONS.map(opt => <button key={opt} type="button" onClick={() => handleRoomToggle(opt)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${selectedRooms.includes(opt) ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-4463">
                            {opt}
                          </button>)}
                      </div>
                      {errors.rooms && <p className="text-xs text-red-500 mt-2 flex items-center space-x-1" data-id="element-4464">
                          <span data-id="element-4465">⚠️</span>
                          <span data-id="element-4466">{errors.rooms}</span>
                        </p>}
                    </div>
                  </div>

                  <div data-id="element-4467">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4468">
                      Buget (EUR) <span className="text-red-500" data-id="element-4469">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4470">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4471">
                        <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-4472">
                          Minim
                        </label>
                        <div className="relative" data-id="element-4473">
                          <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4474" />
                          <input type="number" value={budgetMin} onChange={e => setBudgetMin(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.budgetMin ? 'border-red-300' : 'border-slate-200'}`} placeholder="400" data-id="element-4475" />
                        </div>
                        {errors.budgetMin && <p className="text-xs text-red-500 mt-1" data-id="element-4476">
                            {errors.budgetMin}
                          </p>}
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-4477">
                        <label className="block text-xs text-emerald-600 mb-2 font-medium" data-id="element-4478">
                          Maxim
                        </label>
                        <div className="relative" data-id="element-4479">
                          <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" data-id="element-4480" />
                          <input type="number" value={budgetMax} onChange={e => setBudgetMax(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.budgetMax ? 'border-red-300' : 'border-emerald-200'}`} placeholder="500" data-id="element-4481" />
                        </div>
                        {errors.budgetMax && <p className="text-xs text-red-500 mt-1" data-id="element-4482">
                            {errors.budgetMax}
                          </p>}
                      </div>
                    </div>
                  </div>

                  <div data-id="element-4483">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4484">
                      Suprafață (m²) <span className="text-red-500" data-id="element-4485">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4486">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4487">
                        <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-4488">
                          Minim
                        </label>
                        <div className="relative" data-id="element-4489">
                          <HomeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4490" />
                          <input type="number" value={surfaceMin} onChange={e => setSurfaceMin(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.surfaceMin ? 'border-red-300' : 'border-slate-200'}`} placeholder="50" data-id="element-4491" />
                        </div>
                        {errors.surfaceMin && <p className="text-xs text-red-500 mt-1" data-id="element-4492">
                            {errors.surfaceMin}
                          </p>}
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-4493">
                        <label className="block text-xs text-blue-600 mb-2 font-medium" data-id="element-4494">
                          Maxim
                        </label>
                        <div className="relative" data-id="element-4495">
                          <HomeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" data-id="element-4496" />
                          <input type="number" value={surfaceMax} onChange={e => setSurfaceMax(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white ${errors.surfaceMax ? 'border-red-300' : 'border-blue-200'}`} placeholder="75" data-id="element-4497" />
                        </div>
                        {errors.surfaceMax && <p className="text-xs text-red-500 mt-1" data-id="element-4498">
                            {errors.surfaceMax}
                          </p>}
                      </div>
                    </div>
                  </div>

                  <div data-id="element-4499">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4500">
                      Modalitate de Plată
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-4501">
                      <button type="button" onClick={() => handlePaymentMethodToggle('cash')} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${paymentMethods.includes('cash') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-4502">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${paymentMethods.includes('cash') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-4503">
                          {paymentMethods.includes('cash') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-4504" />}
                        </div>
                        <span data-id="element-4505">Cash</span>
                      </button>
                      <button type="button" onClick={() => handlePaymentMethodToggle('credit')} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${paymentMethods.includes('credit') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-4506">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${paymentMethods.includes('credit') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-4507">
                          {paymentMethods.includes('credit') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-4508" />}
                        </div>
                        <span data-id="element-4509">Credit</span>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-4510">
                    <div className="flex items-center justify-between mb-2" data-id="element-4511">
                      <label className="block text-xs font-medium text-slate-500" data-id="element-4512">
                        Zone Preferate (minim 3){' '}
                        <span className="text-red-500" data-id="element-4513">*</span>
                      </label>
                      {zones.length < 5 && <button onClick={addZone} className="text-xs flex items-center gap-1 text-emerald-600 font-bold hover:bg-emerald-50 px-2 py-1 rounded-lg transition-colors" data-id="element-4514">
                          <PlusIcon className="w-3 h-3" data-id="element-4515" /> Adaugă Zonă
                        </button>}
                    </div>

                    <div className="space-y-2" data-id="element-4516">
                      {zones.map((zone, idx) => <div key={idx} className="flex gap-2" data-id="element-4517">
                          <div className="relative flex-1" data-id="element-4518">
                            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4519" />
                            <input type="text" value={zone} onChange={e => handleZoneChange(idx, e.target.value)} className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none ${errors.zones && !zone.trim() ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder={`Zona ${idx + 1}`} data-id="element-4520" />
                          </div>
                          {zones.length > 3 && <button onClick={() => removeZone(idx)} className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors" data-id="element-4521">
                              <TrashIcon className="w-4 h-4" data-id="element-4522" />
                            </button>}
                        </div>)}
                    </div>
                    {errors.zones && <p className="text-xs text-red-500 mt-2 flex items-center space-x-1" data-id="element-4523">
                        <span data-id="element-4524">⚠️</span>
                        <span data-id="element-4525">{errors.zones}</span>
                      </p>}
                  </div>
                </div>
              </motion.div>

              {/* Optional Fields Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4526">
                <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-4527">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center" data-id="element-4528">
                    <HomeIcon className="w-5 h-5 text-slate-600" data-id="element-4529" />
                  </div>
                  <span data-id="element-4530">Câmpuri Opționale</span>
                </h5>

                <div className="space-y-4" data-id="element-4531">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4532">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4533">
                      Etaje Excluse
                    </label>
                    <input type="text" value={excludedFloors} onChange={e => setExcludedFloors(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: parter, etaj 1" data-id="element-4534" />
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4535">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4536">
                      Nr. Maxim de Etaje
                    </label>
                    <input type="number" value={maxFloors} onChange={e => setMaxFloors(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: 5" data-id="element-4537" />
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4538">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4539">
                      Nr. Băi
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-4540">
                      {BATHROOM_OPTIONS.map(opt => <button key={opt} type="button" onClick={() => handleBathroomToggle(opt)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${selectedBathrooms.includes(opt) ? 'bg-slate-600 text-white border-slate-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-4541">
                          {opt}
                        </button>)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4542">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4543">
                      An Construcție
                    </label>
                    <input type="number" value={constructionYear} onChange={e => setConstructionYear(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: 2015" data-id="element-4544" />
                  </div>
                </div>
              </motion.div>

              {/* Qualification Questions Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4545">
                <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-4546">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-4547">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600" data-id="element-4548" />
                  </div>
                  <span data-id="element-4549">Întrebări Calificare</span>
                </h5>

                <div className="space-y-4" data-id="element-4550">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4551">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4552">
                      Cine se va muta în noul imobil?{' '}
                      <span className="text-red-500" data-id="element-4553">*</span>
                    </label>
                    <input type="text" value={whoWillMove} onChange={e => setWhoWillMove(e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white ${errors.whoWillMove ? 'border-red-300' : 'border-slate-200'}`} placeholder="ex: Familie cu 2 copii, Persoană singură, Cuplu tânăr..." data-id="element-4554" />
                    {errors.whoWillMove && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-4555">
                        <span data-id="element-4556">⚠️</span>
                        <span data-id="element-4557">{errors.whoWillMove}</span>
                      </p>}
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4558">
                    <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-4559">
                      De ce se dorește o altă achiziție de imobil?{' '}
                      <span className="text-red-500" data-id="element-4560">*</span>
                    </label>
                    <textarea value={acquisitionReason} onChange={e => setAcquisitionReason(e.target.value)} rows={3} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none bg-white ${errors.acquisitionReason ? 'border-red-300' : 'border-slate-200'}`} placeholder="ex: Mutare în alt oraș, Spațiu mai mare, Aproape de serviciu..." data-id="element-4561" />
                    {errors.acquisitionReason && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-4562">
                        <span data-id="element-4563">⚠️</span>
                        <span data-id="element-4564">{errors.acquisitionReason}</span>
                      </p>}
                  </div>
                </div>
              </motion.div>

              {/* Memo Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-4565">
                <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-4566">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-4567">
                    <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-4568" />
                  </div>
                  <span data-id="element-4569">Memo Calificare</span>
                </h5>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-4570">
                  <textarea value={memo} onChange={e => setMemo(e.target.value)} rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none bg-white" placeholder="Alte detalii relevante despre client..." data-id="element-4571" />
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="flex space-x-3 pb-6" data-id="element-4572">
                <button onClick={onClose} className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 bg-white border-2 border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-4573">
                  Anulează
                </button>
                <button onClick={handleSubmit} className="flex-1 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center gap-2" data-id="element-4574">
                  <SaveIcon className="w-5 h-5" data-id="element-4575" />
                  Salvează Lead
                </button>
              </motion.div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}